import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import {
    getCatalogFinGroupsDetailThunk,
    getCatalogFinGroupsThunk,
    getExportExportPaymentsThunk,
    getExportExportPromosThunk,
    getExportExportSchedulesThunk,
    getInvoiceDebtListThunk,
    getInvoiceSalesByMarketsThunk,
    getInvoiceSalesBySKUThunk,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getMarketInfoCollectionThunk,
    getOrderClosedCheckoutListThunk,
    getOrderOpenedCheckoutListThunk,
    getSchedulerAllScheduleThunk,
    getSchedulerDayScheduleExtraThunk,
    getSchedulerDayScheduleThunk,
    getTaskListsThunk,
    getWorkCheckThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
    postTaskAddDebtTaskThunk,
    postTaskAddDistributionPricesThunk,
    postTaskAddRejectTaskThunk,
    postTaskAddSaleTaskThunk,
    postWorkStartThunk,
    postWorkStopThunk,
} from "tm-shared/api";
import {
    AnyTask,
    CheckoutOrder,
    Market,
    PaymentForm,
    ProductOrderItemDetails,
    VerifierQrPayloadWithScanMeta,
    WorkStatus,
} from "tm-shared/types";

import { ContractFormValues } from "@/types/form";

import { getMarketInfoCollectionUnplannedThunk } from "./work.slice.thunks";
import { WorkState } from "./work.slice.types";

const initialState: WorkState = {
    marketExtraIds: [],
    markets: {},
    marketIds: [],
    tasks: {},
    taskMarketMap: {},
    workStatus: "",
    checkoutList: {},
    checkoutCompletedList: {},
    mmlStatistics: null,
    mmlStatisticsDate: "",
    mmlStatisticsError: "",
    motivation: null,
    motivationError: "",
    salary: null,
    salaryError: "",
    oldExportedPaymentsSum: 0,
    skuSales: [],
    marketsSales: [],
    exportIndicator: 0,
    supervisorCheck: {},
    // new
    currentRouteList: [],
    unplannedMarkets: {
        markets: {},
    },
    contractFormValues: null,
    debts: {},
    options: {
        finGroups: [],
        finGroupsDetail: [],
    },
};

const workSlice = createSlice({
    name: "work",
    initialState,
    reducers: {
        setTaskCompleted: (
            state,
            action: PayloadAction<{
                taskId: string;
                marketId: string;
                value?: boolean;
            }>,
        ) => {
            const { taskId, marketId, value = true } = action.payload;
            if (state.tasks[marketId] && state.tasks[marketId][taskId]) {
                state.tasks[marketId][taskId].completed = value;
            }
        },

        addItemToCheckout: (
            state,
            action: PayloadAction<{
                checkoutList: Record<string, CheckoutOrder>;
            }>,
        ) => {
            state.checkoutList = {
                ...state.checkoutList,
                ...action.payload.checkoutList,
            };
        },
        clearMarkets: (state) => {
            state.markets = {};
        },
        clearTasks: (state) => {
            state.tasks = {};
        },
        clearMainCheckout: (state) => {
            state.checkoutList = {};
            state.checkoutCompletedList = {};
        },
        clearCheckoutItem: (state, action: PayloadAction<string[]>) => {
            action.payload?.forEach((item) => {
                delete state.checkoutList[item];
            });
        },

        setCheckoutProducts: (
            state,
            action: PayloadAction<{
                orderId: string;
                products: Record<string, ProductOrderItemDetails>;
            }>,
        ) => {
            const { orderId, products } = action.payload;
            if (!state.checkoutList[orderId]) return;

            state.checkoutList[orderId].products = products;
        },

        changeOldExportedPaymentsSum: (
            state,
            action: PayloadAction<number>,
        ) => {
            state.oldExportedPaymentsSum = action.payload;
        },
        addAdditionalOfflineTask: (
            state,
            action: PayloadAction<
                Omit<AnyTask, "info" | "targetDate" | "targetType">
            >,
        ) => {
            const { id: taskId, targetId } = action.payload;

            if (!state.tasks[targetId]) {
                state.tasks[targetId] = {};
            }

            if (!state.tasks[targetId][taskId]) {
                state.tasks[targetId][taskId] = {
                    ...action.payload,
                    targetDate: "",
                    targetType: "market",
                    info: {
                        performer: {
                            userId: "",
                            name: "",
                            role: "",
                            roleSlug: "",
                            phone: "",
                        },
                    },
                } as AnyTask;

                state.taskMarketMap[taskId] = targetId;
            }
        },
        changeCheckoutForm: (state, action: PayloadAction<string>) => {
            const checkout = state.checkoutList[action.payload];

            if (!checkout) return;

            checkout.paymentForm =
                checkout.paymentForm === PaymentForm.Cash1
                    ? PaymentForm.Card2
                    : PaymentForm.Cash1;
        },

        changeCheckoutShipmentDate: (
            state,
            action: PayloadAction<{
                orderId: string;
                date: string;
            }>,
        ) => {
            const checkout = state.checkoutList[action.payload.orderId];

            if (!checkout) return;

            checkout.customShipmentDate = action.payload.date;
        },

        increaseExportIndicator(
            state,
            action: PayloadAction<number | string | undefined>,
        ) {
            const value = action.payload ?? 1;

            if (
                typeof value === "number" &&
                typeof state.exportIndicator === "number"
            ) {
                state.exportIndicator += value;
            } else {
                state.exportIndicator = value;
            }
        },

        decreaseExportIndicator(state) {
            const step = 1;

            if (typeof state.exportIndicator === "number") {
                const next = state.exportIndicator - step;
                state.exportIndicator = next < 0 ? 0 : next;
            } else {
                state.exportIndicator = 0;
            }
        },
        setSupervisorCheck(
            state,
            action: PayloadAction<{
                taskId: string;
                payload: VerifierQrPayloadWithScanMeta;
            }>,
        ) {
            const { taskId, payload } = action.payload;

            state.supervisorCheck[taskId] = payload;
        },
        removeSupervisorCheck(state, action: PayloadAction<string>) {
            const taskId = action.payload;

            delete state.supervisorCheck[taskId];
        },

        setContractFormValues: (
            state,
            action: PayloadAction<ContractFormValues | null>,
        ) => {
            state.contractFormValues = action.payload;
        },
        patchContractFormValues: (
            state,
            action: PayloadAction<Partial<ContractFormValues>>,
        ) => {
            state.contractFormValues = {
                ...state.contractFormValues,
                ...action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInvoiceDebtListThunk.fulfilled, (state, action) => {
                state.debts = action.payload.result.reduce((acc, debt) => {
                    if (!acc[debt.consignmentId]) {
                        acc[debt.consignmentId] = {};
                    }
                    acc[debt.consignmentId] = debt;
                    return acc;
                }, {});
            })

            .addCase(
                getMarketInfoCollectionThunk.fulfilled,
                (state, action: PayloadAction<{ result: Market[] }>) => {
                    state.markets = {
                        ...state.markets,
                        ...action.payload.result.reduce(
                            (acc, market) => {
                                acc[market.id] = market;
                                return acc;
                            },
                            {} as Record<string, Market>,
                        ),
                    };
                },
            )

            .addCase(getTaskListsThunk.fulfilled, (state, action) => {
                state.tasks = action.payload.result.reduce((acc, task) => {
                    if (!acc[task.targetId]) {
                        acc[task.targetId] = {};
                    }
                    acc[task.targetId][task.id] = task;
                    state.taskMarketMap[task.id] = task.targetId;
                    return acc;
                }, {});
            })

            .addCase(
                getSchedulerDayScheduleThunk.fulfilled,
                (state, action: PayloadAction<{ result: string[] }>) => {
                    state.marketIds = action.payload.result.filter(
                        (item) => !state.marketExtraIds?.includes(item),
                    );
                },
            )
            .addCase(
                getSchedulerDayScheduleExtraThunk.fulfilled,
                (state, action) => {
                    state.marketExtraIds = action.payload.result;
                },
            )

            .addCase(
                getWorkStartAndGetScheduleThunk.fulfilled,
                (state, action) => {
                    state.workStatus = !!action.payload.result?.tasks?.length;
                    state.tasks = action.payload.result.tasks.reduce(
                        (acc, task) => {
                            if (!acc[task.targetId]) {
                                acc[task.targetId] = {};
                            }
                            state.taskMarketMap[task.id] = task.targetId;
                            acc[task.targetId][task.id] = task;
                            return acc;
                        },
                        {},
                    );
                    state.markets = action.payload.result.markets;
                    state.workStatus = WorkStatus.StartWork;
                },
            )

            .addCase(postWorkStopThunk.fulfilled, (state) => {
                state.workStatus = WorkStatus.EndWork;
            })

            .addCase(postWorkStartThunk.fulfilled, (state) => {
                state.workStatus = WorkStatus.StartWork;
            })

            .addCase(
                getWorkCheckThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<{ result: string | boolean }>,
                ) => {
                    state.workStatus = action.payload.result;
                },
            )

            .addCase(
                getOrderOpenedCheckoutListThunk.fulfilled,
                (state, action) => {
                    const checkoutList = action.payload.result.reduce(
                        (acc, order) => {
                            if (acc[order.orderId]) {
                                return acc;
                            }

                            const products = Object.values(
                                order.products,
                            ).reduce((productAcc, product) => {
                                productAcc[product.barcode] = {
                                    ...product,
                                    apiCount: product.count,
                                };

                                return productAcc;
                            }, {});

                            acc[order.orderId] = {
                                ...order,
                                products,
                                closed: false,
                            };

                            return acc;
                        },
                        {},
                    );

                    state.checkoutList = checkoutList;
                },
            )

            .addCase(
                getOrderClosedCheckoutListThunk.fulfilled,
                (state, action) => {
                    state.checkoutCompletedList = action.payload.result.reduce(
                        (acc, order) => {
                            if (!acc[order.orderId]) {
                                acc[order.orderId] = {
                                    ...order,
                                    products: Object.values(
                                        order.products,
                                    ).reduce((acc, product) => {
                                        acc[product.barcode] = {
                                            ...product,
                                            apiCount: product.count,
                                        };
                                        return acc;
                                    }, {}),
                                    closed: true,
                                };
                            }
                            return acc;
                        },
                        {},
                    );
                },
            )

            .addCase(getKpiGetProgressThunk.fulfilled, (state, action) => {
                state.motivation = action.payload.result;
                state.motivationError = "";
            })

            .addCase(getKpiGetProgressThunk.rejected, (state) => {
                state.motivationError =
                    "План мотивації наразі не розраховано! Для уточнення зверніться до керівника";
                state.motivation = null;
            })

            .addCase(getKpiGetSalaryToPayThunk.fulfilled, (state, action) => {
                state.salary = action.payload.result;
                state.salaryError = "";
            })
            .addCase(getKpiGetSalaryToPayThunk.rejected, (state) => {
                state.salaryError =
                    "Зарплатні дані наразі відсутні! Для уточнення зверніться до керівника";
                state.salary = null;
            })

            .addCase(getInvoiceSalesBySKUThunk.fulfilled, (state, action) => {
                state.skuSales = action.payload.result;
            })

            .addCase(getInvoiceSalesBySKUThunk.rejected, (state) => {
                state.skuSales = [];
            })

            .addCase(
                getInvoiceSalesByMarketsThunk.fulfilled,
                (state, action) => {
                    state.marketsSales = action.payload.result;
                },
            )
            .addCase(getInvoiceSalesByMarketsThunk.rejected, (state) => {
                state.marketsSales = [];
            })

            .addCase(getExportExportSchedulesThunk.pending, (state, action) => {
                if (
                    action.meta.arg.agentId &&
                    typeof state.exportIndicator === "number"
                ) {
                    state.exportIndicator += 1;
                } else {
                    state.exportIndicator = "A";
                }
            })

            .addCase(
                getSchedulerAllScheduleThunk.fulfilled,
                (state, { payload }) => {
                    state.currentRouteList = payload.result;
                },
            )

            .addCase(
                getMarketInfoCollectionUnplannedThunk.fulfilled,
                (state, { payload }) => {
                    state.unplannedMarkets.markets = payload.result?.reduce(
                        (acc, market) => {
                            if (!acc[market?.id]) {
                                acc[market?.id] = {};
                            }
                            acc[market?.id] = market;
                            return acc;
                        },
                        {},
                    );
                },
            )

            .addCase(getCatalogFinGroupsThunk.fulfilled, (state, action) => {
                state.options.finGroups = action.payload.result.map((item) => ({
                    value: item,
                    label: item,
                }));
            })
            .addCase(
                getCatalogFinGroupsDetailThunk.fulfilled,
                (state, action) => {
                    state.options.finGroupsDetail = action.payload.result.map(
                        (item) => ({
                            value: item,
                            label: item,
                        }),
                    );
                },
            )

            .addMatcher(
                isAnyOf(
                    getWorkGetScheduleThunk.fulfilled,
                    getWorkStartAndGetScheduleThunk.fulfilled,
                ),
                (state, action) => {
                    const { tasks, dayScheduleExtra, markets } =
                        action.payload.result;

                    const tasksMap: Record<
                        string,
                        Record<string, AnyTask>
                    > = {};
                    for (const task of tasks) {
                        if (!tasksMap[task.targetId]) {
                            tasksMap[task.targetId] = {};
                        }
                        tasksMap[task.targetId][task.id] = task;
                        state.taskMarketMap[task.id] = task.targetId;
                    }
                    state.tasks = tasksMap;

                    const extraIdsSet = new Set(dayScheduleExtra);
                    state.marketExtraIds = dayScheduleExtra;

                    const marketIds: string[] = [];
                    const marketsMap: Record<string, Market> = {};

                    for (const [id, market] of Object.entries(markets)) {
                        const isUnplanned = extraIdsSet.has(id);

                        if (!isUnplanned) {
                            marketIds.push(id);
                        }

                        marketsMap[id] = {
                            ...market,
                            unplanned: isUnplanned,
                        };
                    }

                    state.marketIds = marketIds;
                    state.markets = marketsMap;
                },
            )

            .addMatcher(
                isAnyOf(
                    postTaskAddDebtTaskThunk.fulfilled,
                    postTaskAddDistributionPricesThunk.fulfilled,
                    postTaskAddSaleTaskThunk.fulfilled,
                    postTaskAddRejectTaskThunk.fulfilled,
                ),
                (state, { payload }) => {
                    if (!payload.result) return;

                    const { id, targetId } = payload.result;
                    state.tasks[targetId][id] = payload.result;
                    state.taskMarketMap[id] = targetId;
                },
            )
            .addMatcher(
                isAnyOf(
                    getExportExportPromosThunk.pending,
                    getExportExportPaymentsThunk.pending,
                ),
                (state) => {
                    state.exportIndicator = "P";
                },
            )

            .addMatcher(
                isAnyOf(
                    getExportExportSchedulesThunk.rejected,
                    getExportExportPromosThunk.rejected,
                    getExportExportPaymentsThunk.rejected,
                ),
                (state) => {
                    if (typeof state.exportIndicator === "string") {
                        state.exportIndicator = 0;
                    } else {
                        state.exportIndicator -= 1;
                    }
                },
            );
    },
});

export const {
    setTaskCompleted,
    clearMarkets,
    clearCheckoutItem,
    clearTasks,
    clearMainCheckout,
    addItemToCheckout,
    changeOldExportedPaymentsSum,
    addAdditionalOfflineTask,
    changeCheckoutForm,
    setCheckoutProducts,
    increaseExportIndicator,
    decreaseExportIndicator,
    changeCheckoutShipmentDate,
    setSupervisorCheck,
    removeSupervisorCheck,
    setContractFormValues,
    patchContractFormValues,
} = workSlice.actions;

export default workSlice.reducer;
